<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { directive as onClickaway } from 'vue3-click-away';
import NextButton from 'dashboard/components-next/button/Button.vue';

const vOnClickaway = onClickaway;

const props = defineProps({
  macroName: {
    type: String,
    required: true,
  },
  prompts: {
    type: Array,
    required: true,
  },
  isExecuting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit', 'cancel']);
const { t } = useI18n();

const values = ref({});
const firstInputRef = ref(null);

watch(
  () => props.prompts,
  next => {
    const seed = {};
    next.forEach(p => {
      seed[p.name] = values.value[p.name] ?? '';
    });
    values.value = seed;
    nextTick(() => firstInputRef.value?.focus());
  },
  { immediate: true }
);

const allRequiredFilled = computed(() =>
  props.prompts.every(p => (values.value[p.name] || '').trim() !== '')
);

const submit = () => {
  if (!allRequiredFilled.value || props.isExecuting) return;
  const trimmed = {};
  Object.entries(values.value).forEach(([k, v]) => {
    trimmed[k] = String(v).trim();
  });
  emit('submit', trimmed);
};

const cancel = () => emit('cancel');
</script>

<template>
  <div
    v-on-clickaway="cancel"
    class="absolute z-50 bottom-8 right-8 w-72 rounded-md border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg p-4 text-left rtl:text-right"
  >
    <h6 class="mb-3 text-sm text-n-slate-12 font-medium">
      {{ macroName }}
    </h6>
    <form class="flex flex-col gap-3" @submit.prevent="submit">
      <div
        v-for="(p, idx) in prompts"
        :key="p.name"
        class="flex flex-col gap-1"
      >
        <label
          :for="`macro-prompt-${p.name}`"
          class="text-xs text-n-slate-11"
        >
          {{ p.label }}
        </label>
        <input
          :id="`macro-prompt-${p.name}`"
          :ref="el => { if (idx === 0) firstInputRef = el }"
          v-model="values[p.name]"
          :type="p.type === 'number' ? 'number' : 'text'"
          :placeholder="p.label"
          autocomplete="off"
          spellcheck="false"
          class="w-full px-2 py-1.5 text-sm rounded border border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-n-brand"
          @keyup.enter="submit"
        />
      </div>
      <div class="flex gap-2 justify-end mt-1">
        <NextButton
          type="button"
          sm
          slate
          faded
          :label="t('MACROS.PROMPT_DIALOG.CANCEL')"
          @click="cancel"
        />
        <NextButton
          type="submit"
          sm
          :is-loading="isExecuting"
          :disabled="!allRequiredFilled"
          :label="t('MACROS.PROMPT_DIALOG.SUBMIT')"
        />
      </div>
    </form>
  </div>
</template>
